// @flow
import React from 'react';

type Props = {
    disabled: boolean,
    onChange: (value: ?string) => void,
    onFinish: () => void,
    schemaOptions: Object,
    value: ?string,
};

type State = {
    suggestions: Array<string>,
    filteredSuggestions: Array<string>,
    showSuggestions: boolean,
    highlightedIndex: number,
    inputValue: string,
};

export default class RoleAutocomplete extends React.Component<Props, State> {
    inputRef: ?HTMLInputElement;

    constructor(props: Props) {
        super(props);
        this.state = {
            suggestions: [],
            filteredSuggestions: [],
            showSuggestions: false,
            highlightedIndex: -1,
            inputValue: props.value || '',
        };
    }

    componentDidMount() {
        this.fetchSuggestions();
    }

    componentDidUpdate(prevProps: Props) {
        if (prevProps.value !== this.props.value && this.props.value !== this.state.inputValue) {
            this.setState({ inputValue: this.props.value || '' });
        }
    }

    fetchSuggestions = () => {
        fetch('/admin/api/event-attendee-roles')
            .then((response) => response.json())
            .then((data) => {
                const roles = data._embedded?.roles || [];
                this.setState({ suggestions: roles });
            })
            .catch((error) => {
                console.error('Error fetching role suggestions:', error);
            });
    };

    filterSuggestions = (inputValue: string): Array<string> => {
        const { suggestions } = this.state;
        if (!inputValue.trim()) {
            return suggestions;
        }
        const lowerInput = inputValue.toLowerCase();
        return suggestions.filter((suggestion) =>
            suggestion.toLowerCase().includes(lowerInput)
        );
    };

    handleInputChange = (event: SyntheticInputEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;
        const filteredSuggestions = this.filterSuggestions(inputValue);

        this.setState({
            inputValue,
            filteredSuggestions,
            showSuggestions: true,
            highlightedIndex: -1,
        });

        this.props.onChange(inputValue);
    };

    handleInputFocus = () => {
        const { inputValue } = this.state;
        const filteredSuggestions = this.filterSuggestions(inputValue);
        this.setState({
            filteredSuggestions,
            showSuggestions: true,
        });
    };

    handleInputBlur = () => {
        // Delay to allow click on suggestion
        setTimeout(() => {
            this.setState({ showSuggestions: false });
            this.props.onFinish();
        }, 200);
    };

    handleKeyDown = (event: SyntheticKeyboardEvent<HTMLInputElement>) => {
        const { filteredSuggestions, highlightedIndex, showSuggestions } = this.state;

        if (!showSuggestions || filteredSuggestions.length === 0) {
            return;
        }

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                this.setState({
                    highlightedIndex: Math.min(
                        highlightedIndex + 1,
                        filteredSuggestions.length - 1
                    ),
                });
                break;
            case 'ArrowUp':
                event.preventDefault();
                this.setState({
                    highlightedIndex: Math.max(highlightedIndex - 1, -1),
                });
                break;
            case 'Enter':
                event.preventDefault();
                if (highlightedIndex >= 0 && highlightedIndex < filteredSuggestions.length) {
                    this.selectSuggestion(filteredSuggestions[highlightedIndex]);
                }
                break;
            case 'Escape':
                this.setState({ showSuggestions: false });
                break;
            default:
                break;
        }
    };

    selectSuggestion = (suggestion: string) => {
        this.setState({
            inputValue: suggestion,
            showSuggestions: false,
            highlightedIndex: -1,
        });
        this.props.onChange(suggestion);
    };

    handleSuggestionClick = (suggestion: string) => {
        this.selectSuggestion(suggestion);
    };

    render() {
        const { disabled, schemaOptions } = this.props;
        const { inputValue, filteredSuggestions, showSuggestions, highlightedIndex } = this.state;

        const maxLength = schemaOptions?.soft_max_length?.value || 120;

        return (
            <div className="role-autocomplete">
                <input
                    ref={(ref) => { this.inputRef = ref; }}
                    type="text"
                    value={inputValue}
                    onChange={this.handleInputChange}
                    onFocus={this.handleInputFocus}
                    onBlur={this.handleInputBlur}
                    onKeyDown={this.handleKeyDown}
                    disabled={disabled}
                    maxLength={maxLength}
                    className="role-autocomplete__input"
                    autoComplete="off"
                />
                {showSuggestions && filteredSuggestions.length > 0 && (
                    <div className="role-autocomplete__suggestions">
                        {filteredSuggestions.map((suggestion, index) => (
                            <div
                                key={suggestion}
                                className={
                                    'role-autocomplete__suggestion' +
                                    (index === highlightedIndex ? ' role-autocomplete__suggestion--highlighted' : '')
                                }
                                onMouseDown={() => this.handleSuggestionClick(suggestion)}
                                onMouseEnter={() => this.setState({ highlightedIndex: index })}
                            >
                                {suggestion}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }
}
